import { PayrollWorkerWithWeeksSummary } from '../../../hooks/payroll'
import { WorkPeriodEvent } from '../../../api/work-period-events'
import { UserSummaries } from '../../../api/missions'
import { getDocIdAsString } from '../../../utils/documents'
import { isSameWeek } from 'date-fns'
import { fr } from 'date-fns/locale'
import { formatDurationAsDecimal } from '../../../utils/date'
import { getRuleValuesFromSummary } from '../../../utils/employer-rules'

/**
 * Calcule les totaux pour les employer rules et benefit events
 */
export const calculateTotals = (
  weeks: Date[],
  worker: PayrollWorkerWithWeeksSummary,
  events: WorkPeriodEvent[],
  employerRules: UserSummaries['weeks'][0]['employerRules'],
): {
  totalBenefitEvents: Record<string, number>
  totalInformativeEvents: number
  totalInformativeEventsByEvent: Record<string, number>
  totalEmployerRulesValues: Record<string, string | number>
  totalEmployerRulesDecimalValues: Record<string, string | number>
  negativeContractDiff: number
} => {
  // Comptage global des benefit events sur l'ensemble des semaines pour filtrer les colonnes
  const totalBenefitEvents = weeks.reduce(
    (totals, week) => {
      const weekSummary = worker.weeksSummary?.weeks.find(
        w =>
          w.missions[0]?.workPeriods[0] &&
          isSameWeek(week, new Date(w.missions[0]?.workPeriods[0].start.date), {
            locale: fr,
            weekStartsOn: 1,
          }),
      )

      events
        .filter(e => e.type === 'benefit')
        .forEach(event => {
          const eventCount =
            weekSummary?.missions.reduce((totalCount, mission) => {
              return (
                totalCount +
                mission.workPeriods.reduce((missionCount, workPeriod) => {
                  return (
                    missionCount +
                    workPeriod.events.filter(
                      we => getDocIdAsString(we.item) === getDocIdAsString(event),
                    ).length
                  )
                }, 0)
              )
            }, 0) || 0

          totals[`event_${event.code}`] = (totals[`event_${event.code}`] || 0) + eventCount
        })

      return totals
    },
    {} as Record<string, number>,
  )

  // Total des évènements informatifs et détail par event (pour la colonne + popover totaux)
  const totalInformativeEventsByEvent = weeks.reduce(
    (totals, week) => {
      const weekSummary = worker.weeksSummary?.weeks.find(
        w =>
          w.missions[0]?.workPeriods[0] &&
          isSameWeek(week, new Date(w.missions[0]?.workPeriods[0].start.date), {
            locale: fr,
            weekStartsOn: 1,
          }),
      )

      events
        .filter(e => e.type === 'informative')
        .forEach(event => {
          const eventCount =
            weekSummary?.missions.reduce((totalCount, mission) => {
              return (
                totalCount +
                mission.workPeriods.reduce((missionCount, workPeriod) => {
                  return (
                    missionCount +
                    workPeriod.events.filter(
                      we => getDocIdAsString(we.item) === getDocIdAsString(event),
                    ).length
                  )
                }, 0)
              )
            }, 0) || 0

          const id = getDocIdAsString(event)
          totals[id] = (totals[id] || 0) + eventCount
        })

      return totals
    },
    {} as Record<string, number>,
  )
  const totalInformativeEvents = Object.values(totalInformativeEventsByEvent).reduce(
    (sum, n) => sum + n,
    0,
  )

  // Utiliser les totaux de UserSummaries.totals
  const totalEmployerRulesValues =
    worker.weeksSummary?.totals && employerRules
      ? getRuleValuesFromSummary(employerRules, worker.weeksSummary.totals)
      : {}

  const totalEmployerRulesDecimalValues =
    worker.weeksSummary?.totals && employerRules
      ? getRuleValuesFromSummary(employerRules, worker.weeksSummary.totals, formatDurationAsDecimal)
      : {}

  // Le backend ne calcule pas le total des écarts négatifs => on le calcule manuellement pour l'instant
  const negativeContractDiff = worker.weeksSummary?.weeks.reduce((acc, week) => {
    const value =
      week.totals?.durations?.worked?.contract?.diff < 0
        ? week.totals?.durations?.worked?.contract?.diff
        : 0
    return acc + value
  }, 0)

  return {
    totalBenefitEvents,
    totalInformativeEvents,
    totalInformativeEventsByEvent,
    totalEmployerRulesValues,
    totalEmployerRulesDecimalValues,
    negativeContractDiff,
  }
}

/**
 * Filtre les events pour n'afficher que ceux avec des occurrences
 */
export const filterEventsByOccurrence = (
  events: WorkPeriodEvent[],
  totalBenefitEvents: Record<string, number>,
): { visibleBenefitEvents: WorkPeriodEvent[]; hiddenBenefitEvents: WorkPeriodEvent[] } => {
  const allBenefitEvents = events.filter(e => e.type === 'benefit')
  const visibleBenefitEvents = allBenefitEvents.filter(
    e => (totalBenefitEvents[`event_${e.code}`] || 0) > 0,
  )
  const hiddenBenefitEvents = allBenefitEvents.filter(
    e => (totalBenefitEvents[`event_${e.code}`] || 0) === 0,
  )

  return {
    visibleBenefitEvents,
    hiddenBenefitEvents,
  }
}
